<template>
  <div v-on-clickaway="closeSelector" class="relative">
    <button
      class="nav-action-item flex items-center gap-1 p-1 rounded-[5px] hover:bg-gray-100 transition-colors shrink-0 cursor-pointer outline-none border-0 bg-transparent"
      @click="toggleSelector"
    >
      <div class="flex items-center gap-[5px]">
        <span
          class="material-symbols-outlined text-[16px] text-gray-600 leading-none"
        >
          bolt
        </span>
        <span class="text-sm text-gray-600 leading-5 whitespace-nowrap">
          {{ $t('CONVERSATION_SIDEBAR.ACCORDION.MACROS') }}
        </span>
      </div>
      <span
        class="material-symbols-outlined text-[16px] text-gray-600 leading-none"
      >
        expand_more
      </span>
    </button>
    <div
      v-if="isOpenSelector"
      class="absolute top-[calc(100%+8px)] z-20 bg-white rounded-lg w-[530px] h-[300px] shadow-lg flex flex-col border border-gray-100"
      :class="isRTLView ? 'left-0' : 'right-0'"
    >
      <router-link :to="addAccountScoping('settings/macros')">
        <div class="self-assign-btn">
          <span class="material-symbols-outlined text-[24px] text-green-500">
            add
          </span>
          <span>{{ $t('MACROS.NEW_MACRO_BTN') }}</span>
        </div>
      </router-link>
      <div class="flex flex-row">
        <div class="w-1/2 overflow-auto custom-scroll max-h-[260px]">
          <div
            v-for="macro in macros"
            :key="macro.id"
            class="text-gray-600 gap-2 dropdown-item"
            :class="{ active: isActiveMacro(macro) }"
          >
            <span>{{ macro.name }}</span>
            <div class="flex flex-row gap-2">
              <span
                v-tooltip.left-start="$t('MACROS.EXECUTE.PREVIEW')"
                class="material-symbols-outlined info-btn"
                @click="toggleMacroPreview(macro)"
              >
                info
              </span>
              <span
                v-tooltip.left-start="$t('MACROS.EXECUTE.BUTTON_TOOLTIP')"
                class="material-symbols-outlined trigger-btn"
                @click="executeMacro(macro)"
              >
                play_arrow
              </span>
            </div>
          </div>
          <div
            v-if="noResult"
            class="flex items-center justify-center pb-4 px-2.5 text-sm text-gray-400 dark:text-slate-200 flex-1"
          >
            {{ noResultText }}
          </div>
        </div>
        <div class="w-1/2">
          <div
            class="w-[237px] min-h-[220px] max-h-[244px] overflow-y-auto custom-scroll bg-gray-50 px-4 py-3 border border-gray-100 rounded-lg"
          >
            <div class="text-gray-800 text-base font-semibold">
              {{ $t('MACROS.EXECUTE.PREVIEW_TITLE') }}
            </div>
            <div v-if="macroPreview" class="macro-preview mt-5">
              <div class="text-xs text-gray-500 mb-2">
                {{ $t('MACROS.EXECUTE.PREVIEW_ACTIONS_LABEL') }}
              </div>
              <div
                v-for="(action, i) in resolvedMacro"
                :key="i"
                class="relative pl-4 macro-block"
              >
                <div
                  v-if="i !== macroPreview.actions.length - 1"
                  class="top-[13px] absolute -bottom-1 left-[1px] h-full w-px bg-gray-300 dark:bg-slate-600"
                />
                <div
                  class="absolute -left-[3px] top-[6px] w-[9px] h-[9px] rounded-full bg-gray-50 border border-gray-300 dark:border-slate-600 dark:bg-slate-200 "
                />
                <p class="text-sm text-gray-600 dark:text-slate-400 mb-1">
                  {{
                    $t(
                      `MACROS.ACTION_TYPES.${action.actionName
                        .replaceAll(' ', '_')
                        .toUpperCase()}`
                    )
                  }}
                </p>
                <p
                  class="text-gray-800 mb-1 text-sm font-semibold dark:text-slate-100"
                >
                  {{ action.actionValue }}
                </p>
              </div>
            </div>
            <div v-else class="text-sm text-gray-500 mt-2">
              {{ $t('MACROS.EXECUTE.PREVIEW_EMPTY_STATE') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import accountMixin from 'dashboard/mixins/account.js';
import alertMixin from 'shared/mixins/alertMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import {
  resolveActionName,
  resolveTeamIds,
  resolveLabels,
  resolveAgents,
} from 'dashboard/routes/dashboard/settings/macros/macroHelper';

export default {
  mixins: [alertMixin, accountMixin, rtlMixin],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isOpenSelector: false,
      macroPreview: null,
    };
  },
  computed: {
    ...mapGetters({
      macros: ['macros/getMacros'],
      uiFlags: 'macros/getUIFlags',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
    }),
    noResult() {
      return !this.uiFlags.isFetching && this.macros.length === 0;
    },
    noResultText() {
      return this.$t('MACROS.LIST.404');
    },
    resolvedMacro() {
      return this.macroPreview.actions.map(action => {
        return {
          actionName: resolveActionName(action.action_name),
          actionValue: this.getActionValue(
            action.action_name,
            action.action_params
          ),
        };
      });
    },
  },
  mounted() {
    this.$store.dispatch('macros/get');
  },
  methods: {
    toggleSelector() {
      this.isOpenSelector = !this.isOpenSelector;
    },
    closeSelector() {
      this.isOpenSelector = false;
    },
    getActionValue(key, params) {
      const actionsMap = {
        assign_team: resolveTeamIds(this.teams, params),
        add_label: resolveLabels(this.labels, params),
        remove_label: resolveLabels(this.labels, params),
        assign_agent: resolveAgents(this.agents, params),
        mute_conversation: null,
        snooze_conversation: null,
        resolve_conversation: null,
        remove_assigned_team: null,
        send_webhook_event: params[0],
        send_message: params[0],
        send_email_transcript: params[0],
        add_private_note: params[0],
      };
      return actionsMap[key] || '';
    },
    toggleMacroPreview(macro) {
      this.macroPreview = macro;
    },
    isActiveMacro(macro) {
      return this.macroPreview?.id === macro.id;
    },
    async executeMacro(macro) {
      try {
        this.isExecuting = true;
        await this.$store.dispatch('macros/execute', {
          macroId: macro.id,
          conversationIds: [this.conversationId],
        });
        this.$track(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
        this.showAlert(this.$t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
      } catch (error) {
        this.showAlert(this.$t('MACROS.ERROR'));
      } finally {
        this.isExecuting = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.dropdown-item {
  @apply px-3 py-[3px] flex flex-row items-center justify-between relative select-none;

  &:hover {
    @apply bg-[#F0FDF4] hover:text-[#1F845A];
  }

  &.active {
    @apply bg-[#F0FDF4] text-gray-600;

    .info-btn {
      @apply border-[#1F845A] text-[#1F845A];
    }
  }

  &.disabled {
    @apply text-gray-400 hover:text-gray-400 hover:bg-transparent focus:bg-transparent focus:text-gray-400 cursor-not-allowed;
  }

  .info-btn {
    @apply cursor-pointer text-base/[16px] p-[7px] rounded-[5px] text-gray-500 bg-white border border-gray-200;
  }
  .trigger-btn {
    @apply cursor-pointer text-base/[16px] p-2 rounded-[5px] text-white bg-green-500;
  }
}

.self-assign-btn {
  @apply flex flex-row items-center gap-1 py-2 px-3 sticky left-0 top-0 z-10 text-base;
  @apply bg-white text-green-500 hover:bg-[#F0FDF4] hover:text-[#1F845A] cursor-pointer;

  &:hover .material-symbols-outlined {
    @apply text-[#1F845A];
  }
}

.macro-preview {
  .macro-block {
    &:not(:last-child) {
      @apply pb-2;
    }
  }
}
</style>
